import { ModuleWithProviders, NgModule, inject, provideAppInitializer } from '@angular/core';
import { ApiService } from './providers/api.service';
import { V3dOrthographicViewComponent } from './components/v3d-orthographic-view/v3d-orthographic-view.component';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { WebGLRendererHelper } from './providers/webgl-renderer-helper';
import { V3dPerspectiveViewComponent } from './components/v3d-perspective-view/v3d-perspective-view.component';
import { ConfigService } from './providers/config.service';
import { Visualize3dComponentFactoryService } from './providers/visualize3d-component-factory.service';

type InitFunction = (configService: ConfigService) => () => Promise<void>;

/**
 * Used for initializing the application. ApiRoot is provided as .forRoot() parameter
 * @param apiRoot - Backend API root URL for the 3D view
 * @param throwOnQpdErrors - If true, errors from the QPD (QModel) will be thrown as exceptions, otherwise they will be skipped
 */
export function createAppInitializer(apiRoot: string, throwOnQpdErrors: boolean): InitFunction {
  /*
   * The local variable 'fn' seems redundant, but it is not!
   * It's required for Angular AOT compilation to work.
   * See: https://github.com/ng-packagr/ng-packagr/issues/696#issuecomment-387114613
   */
  // noinspection UnnecessaryLocalVariableJS
  const fn = function appInitializer(configService: ConfigService) {
    return (): Promise<void> => {
      return new Promise(resolve => {
        configService.setApiRoot(apiRoot);
        configService.setThrowOnQpdErrors(throwOnQpdErrors);
        resolve();
      });
    };
  };

  return fn;
}

@NgModule({
  declarations: [V3dOrthographicViewComponent, V3dPerspectiveViewComponent],
  providers: [
    ApiService,
    ConfigService,
    WebGLRendererHelper,
    Visualize3dComponentFactoryService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  exports: [V3dOrthographicViewComponent, V3dPerspectiveViewComponent],
})
export class Visualize3dModule {
  /**
   * Initialize module
   * @param apiRootUrl Root URL for the RESTful API which provides run-time assets for the 3d view
   * @param throwOnQpdErrors - If true, errors from the QPD (QModel) will be thrown as exceptions, otherwise they will be skipped
   */
  public static forRoot(apiRootUrl: string, throwOnQpdErrors: boolean): ModuleWithProviders<Visualize3dModule> {
    return {
      ngModule: Visualize3dModule,
      providers: [
        provideAppInitializer(() => {
          const initializerFn = createAppInitializer(apiRootUrl, throwOnQpdErrors)(inject(ConfigService));
          return initializerFn();
        }),
      ],
    };
  }
}
